import React, { useId, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, ExternalLink, FileText, Loader2, UploadCloud, X } from 'lucide-react';
import { authFetch } from '../api';

interface Props {
  jobId?: string;
  clientId?: string;
  compact?: boolean;
  title?: string;
  onUploaded?: (document: { id: string; url: string; title: string; type: string; size: number }) => void;
}

interface UploadItem {
  id: string;
  name: string;
  status: 'uploading' | 'done' | 'error';
  url?: string;
  error?: string;
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/heic,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const MAX_BYTES = 25 * 1024 * 1024;

const readAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('Could not read that file.'));
  reader.readAsDataURL(file);
});

/**
 * Drag-and-drop file box for property photos, permits, estimates and docs.
 * Files are uploaded through the authenticated server endpoint; the returned
 * URL and metadata are written to the staff Document Vault.
 */
const FileDrop: React.FC<Props> = ({ jobId, clientId, compact = false, title = 'File Drop', onUploaded }) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [error, setError] = useState('');

  const uploadFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setError('');
    const accepted = files.filter(file => {
      if (file.size > MAX_BYTES) {
        setItems(prev => [...prev, { id: `${file.name}-${Date.now()}`, name: file.name, status: 'error', error: 'Over 25 MB' }]);
        return false;
      }
      return true;
    });
    for (const file of accepted) {
      const itemId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setItems(prev => [...prev, { id: itemId, name: file.name, status: 'uploading' }]);
      try {
        const dataUrl = await readAsDataUrl(file);
        const response = await authFetch('/api/uploads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            dataUrl,
            jobId,
            clientId,
            title: file.name,
          }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || `Upload failed (HTTP ${response.status})`);
        setItems(prev => prev.map(item => item.id === itemId ? { ...item, status: 'done', url: body.url } : item));
        onUploaded?.(body);
      } catch (err: any) {
        setItems(prev => prev.map(item => item.id === itemId ? { ...item, status: 'error', error: err?.message || 'Upload failed' } : item));
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="bg-white rounded-[28px] border border-stone-200 p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-stone-400">Easy uploads</p>
          <h3 className="text-lg font-black text-bark mt-1">{title}</h3>
          <p className="text-[11px] text-stone-400 mt-1">Photos, permits, estimates, PDFs, or documents · 25 MB each</p>
        </div>
        <UploadCloud className="w-5 h-5 text-gecko shrink-0" />
      </div>

      <label
        htmlFor={inputId}
        onDragOver={event => { event.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={event => {
          event.preventDefault();
          setDragging(false);
          void uploadFiles(event.dataTransfer.files);
        }}
        className={`block rounded-2xl border-2 border-dashed p-5 sm:p-7 text-center cursor-pointer transition ${
          dragging ? 'border-gecko bg-leaf/10' : 'border-stone-200 bg-stone-50/70 hover:border-gecko/50 hover:bg-stone-50'
        } ${compact ? 'py-5' : ''}`}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={event => { if (event.target.files) void uploadFiles(event.target.files); }}
        />
        <UploadCloud className={`mx-auto ${dragging ? 'text-palm' : 'text-stone-300'} ${compact ? 'w-7 h-7' : 'w-9 h-9'}`} />
        <p className="mt-3 text-sm font-black text-bark">{dragging ? 'Drop it here' : 'Drag files here or click to browse'}</p>
        <p className="text-[10px] text-stone-400 mt-1">Files go straight to the GreenTree Document Vault.</p>
      </label>

      {items.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-2 rounded-xl bg-stone-50 border border-stone-100 px-3 py-2">
              {item.status === 'uploading' && <Loader2 className="w-3.5 h-3.5 text-gecko animate-spin shrink-0" />}
              {item.status === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-gecko shrink-0" />}
              {item.status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
              <FileText className="w-3.5 h-3.5 text-stone-300 shrink-0" />
              <span className="text-[11px] font-bold text-stone-600 truncate flex-1">{item.name}</span>
              {item.status === 'error' && <span className="text-[9px] text-red-500 font-bold truncate max-w-[120px]">{item.error}</span>}
              {item.status === 'done' && item.url && (
                <a href={item.url} target="_blank" rel="noreferrer" className="text-[9px] font-black uppercase tracking-widest text-palm hover:underline shrink-0">
                  Open <ExternalLink className="w-3 h-3 inline" />
                </a>
              )}
              {item.status !== 'uploading' && (
                <button type="button" onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))} aria-label={`Remove ${item.name}`} className="text-stone-300 hover:text-stone-500 shrink-0">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {error && <p className="mt-3 text-[11px] font-bold text-red-600">{error}</p>}
    </div>
  );
};

export default FileDrop;
